import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import Avatar from '@/components/People/Avatar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import FontAwesome5 from "@react-native-vector-icons/fontawesome5";
import { getFile, getMediaFileInfo } from '@/utils/FileManager';
import Reaction from '@/components/Connect/Reaction';
import { useRef } from 'react';
import { addPostReaction, deletePost, getBoardPermissions, hidePost, removePostReaction, togglePostPin } from '@/data/connect';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Hyperlink from 'react-native-hyperlink';
import { openHyperlink } from '@/utils/linking';
import LinkPreview from '@/components/Library/LinkPreview';
import { FlashList } from '@shopify/flash-list';
import { LegendList } from "@legendapp/list";
import SheetModal from '@/components/General/SheetModal';
import ProfileCard from '@/components/People/ProfileCard';
import useMessagesStore from '@/data/store/useMessagesStore';
import usePinnedPostsStore from '@/data/store/usePinnedPostsStore';
import { showAlert } from '@/utils/showAlert';
import { useRouter } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import MediaViewer from '@/components/General/MediaViewer';
import RelativeTime from '@/components/General/RelativeTime';
import { hasMentions } from '@/utils/mentionFormat';
import { renderTextWithMentions } from '@/utils/mentionHelper';
import Animated, { interpolate, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

const CONTAINER_PADDING = 12;
const CONTAINER_MARGIN = 12;


const Post = ({post, showBoardName, variant = 'default', disableNavigation, onDelete}) => {
    const [attachmentMedia, setAttachmentMedia] = useState([])
    const [links, setLinks] = useState([])
    const [reactions, setReactions] = useState([])
    const [viewersModalOpen, setViewersModalOpen] = useState(false)

    const setMessageForReaction = useMessagesStore((state) => state.setMessageForReaction);
    const expandedPostIDs = usePinnedPostsStore(state => state.expandedPostIDs)
    const toggleExpanded = usePinnedPostsStore(state => state.toggleExpanded)

    const { theme, rt } = useUnistyles();
    const router = useRouter();
    // Measure the rendered post width — inside dashboard grid cards the post is
    // much narrower than the screen, and rt.screen.width overstates it.
    const [postWidth, setPostWidth] = useState(0);
    const reactionRef = useRef();

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser()

    const permissions = useMemo(() => getBoardPermissions(post?.Board, loggedInUser), [post?.Board, loggedInUser]);

    //Viewers list is RLS-gated to post author + board admins server-side; the
    //enabled flag just avoids a guaranteed-empty fetch for everyone else.
    const canSeeViewers = post?.authorID == loggedInUser?.id || permissions?.isAdmin;
    const { data: viewers } = Query.posts.viewers({postID: post?.id, options: {enabled: canSeeViewers}});

    const updatePostInQueryCache = (postID, updater) => {
        queryClient.setQueryData(QueryKeys.posts.byBoardId(post?.boardID), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o?.id == postID ? updater(o) : o)}))}
        })
        queryClient.setQueryData(QueryKeys.posts.myBoardsFeed(), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o?.id == postID ? updater(o) : o)}))}
        })
        queryClient.setQueryData(QueryKeys.posts.byId(postID), old => old ? updater(old) : old)
    }

    const deleteMutation = useMutation({
        mutationFn: deletePost,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(post?.boardID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(post?.id)});
            onDelete?.();
        },
    })

    const hidePostMutation = useMutation({
        mutationFn: hidePost,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(post?.boardID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
        },
    })

    const addReactionMutation = useMutation({
        mutationFn: addPostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions || []), {...data, Person: loggedInUser}]}))
        }
    })

    const removeReactionMutation = useMutation({
        mutationFn: removePostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions?.filter(r => !(r.postID == data.postID && r.personID == data.personID)) || [])]}))
        }
    })

    const pinMutation = useMutation({
        mutationFn: togglePostPin,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, isPinned: data.isPinned}))
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(post?.boardID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
        },
    })

    useEffect(() => {
        if(!post) return;
        if(post.attachments?.length > 0){
            let promises = [];

            for(let i=0; i<post.attachments?.length; i++){
                //includeInfo: true so getFile returns {uri} on both web and native.
                //Without it, web's getStorageFile returns `true` (not an object),
                //and results[i].uri is undefined — attachments never render on web.
                promises[i] = getFile(post.attachments[i], true, true);
            }

            Promise.all(promises).then(async (results) => {
                const new_attachments = [];
                for(let i=0; i<results.length; i++){
                    new_attachments[i] = await getMediaFileInfo(results[i]?.uri);
                }

                setAttachmentMedia(new_attachments)
            });
        }
    }, [post?.attachments])

    useEffect(() => {
        if(!post) return;
        if(post.Reactions && post.Reactions.length > 0 && !post.isDeleted){
            const reactionTypes = {}
            post.Reactions.forEach((r) => {
                if(!reactionTypes[r.reaction]){
                    reactionTypes[r.reaction] = []
                }

                reactionTypes[r.reaction].push(r)
            })

            setReactions(reactionTypes)
        }
        else{
            setReactions([])
        }
    }, [JSON.stringify(post)])

    const isPinned = !!post?.isPinned
    const isCollapsed = isPinned && !expandedPostIDs.includes(post?.id)

    const collapseAnim = useSharedValue(isCollapsed ? 0 : 1);
    const headerFadeAnim = useSharedValue(isCollapsed ? 1 : 0);

    useEffect(() => {
        collapseAnim.value = withTiming(isCollapsed ? 0 : 1, { duration: 300 });
        headerFadeAnim.value = withTiming(isCollapsed ? 1 : 0, { duration: 200 });
    }, [isCollapsed]);

    const bodyAnimStyle = useAnimatedStyle(() => ({
        maxHeight: interpolate(collapseAnim.value, [0, 1], [0, 5000]),
        opacity: collapseAnim.value,
        overflow: 'hidden',
    }));

    const collapsedHeaderStyle = useAnimatedStyle(() => ({
        opacity: headerFadeAnim.value,
        pointerEvents: headerFadeAnim.value === 1 ? 'auto' : 'none',
    }));

    const expandedHeaderStyle = useAnimatedStyle(() => ({
        opacity: 1 - headerFadeAnim.value,
    }));

    const headerLayoutStyle = useAnimatedStyle(() => ({
        height: 32 + (16 * collapseAnim.value),
        marginBottom: 2 + (14 * collapseAnim.value),
    }));

    //All hooks above. Bail safely if post never resolved (CHURCH-APP-16C).
    if(!post) return null;

    const postedAt = new Date(post.postedAt);
    const hasPostAttachments = post?.attachments?.length > 0 || attachmentMedia?.length > 0

    const thumbSize = (Math.min(postWidth || rt.screen.width, 700) / 3) - CONTAINER_PADDING - CONTAINER_MARGIN;
    const postAttachments = post?.attachments?.length > 0 && attachmentMedia?.length == 0
    ? post.attachments?.map((a, index) => {
        return <View key={index} style={[styles.placeholderImageContainer, { width: thumbSize, height: thumbSize }]}>
            <Unicon name="image" size={40} colorVariant="border" style={styles.placeholderIcon} />
        </View>
    })
    : attachmentMedia?.length > 0
        ? <MediaViewer media={attachmentMedia} />
        : null

    const messageReactions = Object.entries(reactions)?.map(([emojiName, list]) => {
        return <Reaction key={emojiName} emojiName={emojiName} people={list.map(r => r.Person)} onPress={() => {
            if(!permissions.canReact){
                return false
            }

            const currentUserReaction = list.find(r => r.Person?.id == loggedInUser?.id)

            if(currentUserReaction){
                removeReactionMutation.mutate({postID: post.id, personID: loggedInUser?.id})
            }

            if(currentUserReaction?.reaction != emojiName){
                addReactionMutation.mutate({postID: post.id, reaction: emojiName, personID: loggedInUser?.id})
            }
        }}/>
    })

    let options = [];
    const isAnnouncement = post.Board?.type === 'ANNOUNCEMENTS';
    if(isAnnouncement && loggedInUser?.can?.('Send SMS Messages')) options.push({ name: 'Delivery report', icon: dropdownIcons.info, onSelect: () => router.push(`/connect/announcements/${post.id}`) });
    !isAnnouncement && (permissions.isAdmin || post.authorID == loggedInUser?.id) && options.push({name: "Edit Post", icon: dropdownIcons.edit, onSelect: () => router.push({pathname: `/connect/boards/${post.Board?.id}/posts/create`, params: {id: post.id, boardID: post.Board?.id, boardName: post.Board?.name, type: "MESSAGE_BOARD"}})});
    !isAnnouncement && permissions.isAdmin && options.push({name: post.isPinned ? "Unpin Post" : "Pin Post", icon: post.isPinned ? dropdownIcons.unpin : dropdownIcons.pin, onSelect: () => pinMutation.mutate({postID: post.id, isPinned: !post.isPinned})});
    !isAnnouncement && permissions.isAdmin && options.push({name: post.hidden ? "Reveal Post" : "Hide Post", icon: post.hidden ? dropdownIcons.unhide : dropdownIcons.hide, onSelect: () => hidePostMutation.mutate({postID: post.id, hidden: !post.hidden})});
    !isAnnouncement && (permissions.isAdmin || post.authorID == loggedInUser?.id) && options.push({name: "Delete Post", destructive: true, icon: dropdownIcons.delete, onSelect: () => {
        showAlert({
            title: "Delete Post", 
            subtitle: "Are you sure you want to delete this post? You will not be able to recover it.",
            isDestructive: true,
            confirmText: "Delete",
            onConfirm: () => {
                deleteMutation.mutate({postID: post.id});
            }
        })
    }});

    return (
        <View style={styles.postWrapper} onLayout={(e) => setPostWidth(e.nativeEvent?.layout?.width || 0)}>
            <View style={styles.container(post.hidden, variant, isPinned, isCollapsed)}>
                <View style={styles.headerArea}>
                        {/* Expanded header */}
                        <Animated.View style={[styles.header, isPinned && headerLayoutStyle, isPinned && expandedHeaderStyle]} pointerEvents={isPinned && isCollapsed ? 'none' : 'auto'}>
                            <View style={styles.avatarFrame}>
                                <Avatar size={44} profile={post.author} disableBorder />
                            </View>
                            <View style={styles.headerTextContainer}>
                                <View style={styles.userInfoContainer}>
                                    <Text style={styles.username} numberOfLines={1}>{post.author?.firstName + " " + post.author?.lastName}</Text>
                                    {isPinned && <View style={styles.pinnedStatus}>
                                        <Unicon name="pin" library="material" size={12} colorVariant="textSecondary" testID={`post-pinned-indicator-${post.id}`} />
                                        <Text style={styles.pinnedStatusText}>Pinned</Text>
                                    </View>}
                                </View>
                                <View style={styles.contextRow}>
                                    {post.isPublished === false ? (
                                        <Text style={styles.dateText} numberOfLines={1}>
                                            Posting {(() => {
                                                const now = new Date();
                                                const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                                const startOfPost = new Date(postedAt.getFullYear(), postedAt.getMonth(), postedAt.getDate());
                                                const dayDiff = Math.round((startOfPost - startOfToday) / 86400000);
                                                const timeStr = postedAt.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                                if(dayDiff === 0) return `Today at ${timeStr}`;
                                                if(dayDiff === 1) return `Tomorrow at ${timeStr}`;
                                                if(dayDiff > 1 && dayDiff <= 6) return `${postedAt.toLocaleString('en-US', { weekday: 'long' })} at ${timeStr}`;
                                                return `${postedAt.toLocaleString('en-US', { month: 'short', day: 'numeric' })} at ${timeStr}`;
                                            })()}
                                        </Text>
                                    ) : (
                                        <RelativeTime time={postedAt} style={styles.dateText} />
                                    )}
                                    {showBoardName && <>
                                        <Text style={styles.contextSeparator}>·</Text>
                                        <Chip colorVariant="connect" icon="newspaper-outline" style={styles.boardChip} textStyle={styles.boardChipText} onPress={() => router.push({pathname: `/connect/boards/${post.Board?.id}/feed`})} testID={`post-board-chip-${post.id}`}>{post.Board?.name}</Chip>
                                    </>}
                                    {post.isPublished === false && <Chip icon="time-outline" style={styles.scheduledChip} testID={`post-scheduled-chip-${post.id}`}>Scheduled</Chip>}
                                </View>
                            </View>
                        </Animated.View>

                        {/* Collapsed header overlay (pinned posts only) */}
                        {isPinned && (
                            <Animated.View style={[styles.collapsedHeader, collapsedHeaderStyle]} pointerEvents={isCollapsed ? 'auto' : 'none'}>
                                <Avatar size={24} profile={post.author} disableBorder />
                                <View style={styles.collapsedHeaderText}>
                                    <View style={styles.collapsedTitleRow}>
                                        <Unicon name="pin" library="material" size={12} colorVariant="textSecondary" testID={`post-pinned-indicator-${post.id}`} />
                                        <Text style={styles.collapsedTitle} numberOfLines={1}>{post.title || post.author?.firstName + " " + post.author?.lastName}</Text>
                                    </View>
                                    <Text style={styles.collapsedMetaText} numberOfLines={1}>
                                        {[post.title ? post.author?.firstName + " " + post.author?.lastName : null, post.Board?.name].filter(Boolean).join(' · ')}
                                    </Text>
                                </View>
                            </Animated.View>
                        )}
                    </View>

                    <Animated.View style={bodyAnimStyle} pointerEvents={isCollapsed ? 'none' : 'auto'}>
                        {hasMentions(post.body)
                            ? <>
                                {post.title && <Text style={styles.title} selectable>{post.title}</Text>}
                                <Text style={styles.content} selectable>{renderTextWithMentions(post.body, router)}</Text>
                            </>
                            : <Hyperlink linkText={ url => {
                                    if(!links.includes(url)){
                                        setTimeout(() => setLinks(prev => !prev.includes(url) ? [...prev, url] : prev), 100)
                                    }

                                    return url
                                }} onPress={(url) => openHyperlink(url, router)} linkStyle={{textDecorationLine:"underline"}}>
                                {post.title && <Text style={styles.title} selectable>{post.title}</Text>}
                                <Text style={styles.content} selectable>{post.body}</Text>
                            </Hyperlink>
                        }

                        { links.length > 0 &&
                            <View style={styles.linksContainer}>
                                <FlashList
                                    data={links}
                                    masonry
                                    optimizeItemArrangement
                                    overrideItemLayout={(layout) => {
                                        layout.span = 1;
                                    }}
                                    numColumns={2}
                                    estimatedItemSize={40}
                                    renderItem={({item}) => <View style={styles.linkItem}><LinkPreview invertColors url={item} key={item} /></View>}
                                />
                            </View>
                        }
                    </Animated.View>

                    <Animated.View style={bodyAnimStyle} pointerEvents={isCollapsed ? 'none' : 'auto'}>
                        <View style={styles.attachmentsWrapper(hasPostAttachments)}>{postAttachments}</View>

                        <View style={styles.actions(variant)}>
                            <View style={styles.actionsLeft}>
                                { permissions.canReact &&
                                    <TouchableOpacity
                                        ref={reactionRef}
                                        accessibilityRole="button"
                                        accessibilityLabel="Add reaction"
                                        onPress={() => setMessageForReaction({post,reactionRef,contextMenuOpen: true}) }
                                        style={styles.addReactionButton}
                                    >
                                        <Unicon name="happy-outline" size={17} colorVariant="textSecondary"/>
                                        <Text style={styles.actionLabel}>+</Text>
                                    </TouchableOpacity>
                                }
                                <View style={styles.reactionsGroup}>{messageReactions}</View>
                            </View>
                            { !disableNavigation &&
                                <TouchableOpacity
                                    accessibilityRole="button"
                                    accessibilityLabel={post.Comments?.[0]?.count > 0 ? `${post.Comments[0].count} comments` : 'No comments'}
                                    onPress={() => router.push({pathname: `/connect/boards/${post.Board?.id}/posts/${post.id}`})}
                                    style={[styles.commentPill, !(post.Comments?.[0]?.count > 0) && styles.emptyPill]}
                                >
                                    <FontAwesome5 name="comment" iconStyle="solid" size={14} color={theme.colors.textSecondary}/>
                                    <Text style={styles.commentCount}>{post.Comments?.[0]?.count > 0 ? `${post.Comments[0].count} Comments` : 'No Comments'}</Text>
                                </TouchableOpacity>
                            }
                            { canSeeViewers &&
                                <TouchableOpacity
                                    accessibilityRole="button"
                                    accessibilityLabel={viewers?.length > 0 ? `${viewers.length} views` : 'No views'}
                                    onPress={() => setViewersModalOpen(true)}
                                    style={[styles.commentPill, !(viewers?.length > 0) && styles.emptyPill]}
                                    testID={`post-viewers-${post.id}`}
                                >
                                    <Unicon name="eye-outline" size={15} colorVariant="textSecondary"/>
                                    <Text style={styles.commentCount}>{viewers?.length ?? 0}</Text>
                                </TouchableOpacity>
                            }
                        </View>
                    </Animated.View>
                </View>
                
                {(isPinned || options.length > 0) &&
                    <View style={styles.topRightContainer(isCollapsed)}>
                        {isPinned && (
                            <TouchableOpacity accessibilityRole="button" accessibilityLabel={isCollapsed ? "Expand pinned post" : "Collapse pinned post"} onPress={() => toggleExpanded(post.id)} testID={`post-collapse-toggle-${post.id}`} style={styles.collapseToggle}>
                                <Unicon name={isCollapsed ? "chevron-down-outline" : "chevron-up-outline"} library="ionicon" size={20} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        )}
                        {options.length > 0 && <DropdownMenu options={options} />}
                    </View>
                }

                <SheetModal isOpen={viewersModalOpen} onClose={() => setViewersModalOpen(false)} title="Seen By" snapPoints={[400]}>
                    <LegendList recycleItems keyExtractor={(item) => item.id}
                        data={(viewers || []).map(v => v.Person).filter(Boolean)}
                        estimatedItemSize={50}
                        renderItem={({item}) => <ProfileCard onPress={() => {
                            router.push('/people/' + item?.id)
                            setViewersModalOpen(false)
                        }} profile={item} minimal />}
                        keyboardShouldPersistTaps="handled"
                    />
                </SheetModal>
            </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    container: (hidden, variant, isPinned, isCollapsed) => {
        const isInline = variant === 'alternate';
        const isCompact = rt.breakpoint === 'xs';

        return {
            paddingVertical: isCollapsed ? 6 : (isCompact ? 14 : 18),
            paddingHorizontal: isCompact ? 14 : 20,
            marginHorizontal: isInline || isCompact ? 0 : 12,
            marginVertical: isInline ? 0 : (isCompact ? 4 : 8),
            borderRadius: isInline || isCompact ? 0 : theme.roundness.lg,
            borderBottomWidth: isInline || isCompact ? 1 : 0,
            borderBottomColor: theme.colors.borderSubtle,
            borderLeftWidth: isPinned ? 3 : 0,
            borderLeftColor: isPinned ? theme.colors.connect : 'transparent',
            backgroundColor: theme.colors.surface,
            boxShadow: isInline || isCompact ? theme.shadows.none : theme.shadows.card,
            opacity: hidden ? 0.5 : 1,
        };
    },
    postWrapper: {
        position: 'relative',
        width: '100%',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 48,
        marginBottom: 16,
        paddingRight: 74,
        overflow: 'hidden',
    },
    headerArea: {
        position: 'relative',
    },
    avatarFrame: {
        padding: 2,
        borderRadius: 24,
        backgroundColor: theme.colors.connectContainer,
    },
    headerTextContainer: {
        flex: 1,
        minWidth: 0,
        marginLeft: 12,
    },
    collapsedHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flexDirection: 'row',
        alignItems: 'center',
        paddingRight: 74,
        backgroundColor: theme.colors.surface,
    },
    collapsedHeaderText: {
        flex: 1,
        minWidth: 0,
        marginLeft: 8,
    },
    collapsedTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    collapsedTitle: {
        fontSize: 13,
        color: theme.colors.text,
        fontFamily: theme.fonts.bold,
        flexShrink: 1,
    },
    collapsedMetaText: {
        color: theme.colors.textSecondary,
        fontSize: theme.typography.micro,
        marginTop: 1,
    },
    collapseToggle: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.roundness.sm,
        _web: { _hover: { backgroundColor: theme.colors.hover } },
    },
    username: {
        flexShrink: 1,
        fontSize: 15,
        lineHeight: 20,
        color: theme.colors.text,
        fontFamily: theme.fonts.bold,
    },
    userInfoContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    pinnedStatus: {
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 0,
        gap: 3,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
        backgroundColor: theme.colors.textSecondaryContainer,
    },
    pinnedStatusText: {
        color: theme.colors.textSecondary,
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    contextRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
        minWidth: 0,
        overflow: 'hidden',
        gap: 5,
        marginTop: 2,
    },
    contextSeparator: {
        flexShrink: 0,
        color: theme.colors.textTertiary,
        fontSize: 12,
    },
    boardChip: {
        flexShrink: 1,
        minWidth: 0,
        maxWidth: '45%',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
    },
    boardChipText: {
        flexShrink: 1,
        minWidth: 0,
        fontSize: 10,
    },
    scheduledChip: {
        flexShrink: 0,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
    },
    dateText: {
        flexShrink: 1,
        minWidth: 36,
        color: theme.colors.textSecondary,
        fontSize: 12,
    },
    title: {
        fontSize: rt.breakpoint === 'xs' ? 17 : 19,
        lineHeight: rt.breakpoint === 'xs' ? 22 : 25,
        marginBottom: 8,
        color: theme.colors.text,
        fontFamily: theme.fonts.bold,
    },
    content: {
        fontSize: theme.typography.body,
        lineHeight: 22,
        marginBottom: 8,
        color: theme.colors.text,
    },
    linksContainer: {
        flex: 1,
        marginTop: 12,
    },
    linkItem: {
        margin: 5,
        borderRadius: theme.roundness.sm,
        overflow: 'hidden',
    },
    placeholderImageContainer:{
        borderWidth: 1,
        borderColor: theme.colors.borderSubtle,
        backgroundColor: theme.colors.surfaceVariant,
        width: ((Math.min(rt.screen.width, 700))/3) - CONTAINER_PADDING - CONTAINER_MARGIN,
        height: ((Math.min(rt.screen.width, 700))/3) - CONTAINER_PADDING - CONTAINER_MARGIN,
    },
    placeholderIcon: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: [{ translateX: -20 }, { translateY: -20 }],
    },
    imageContainer: {
        overflow: "hidden",
        borderWidth: 1,
        borderColor: theme.colors.surface,
    },
    image:{
        width: ((Math.min(rt.screen.width, 700))/3) - CONTAINER_PADDING - CONTAINER_MARGIN,
        height: ((Math.min(rt.screen.width, 700))/3) - CONTAINER_PADDING - CONTAINER_MARGIN,
    },
    attachmentsWrapper: (hasAttachments) => ({
        flexDirection: "row",
        flexWrap: "wrap",
        ...(hasAttachments ? {
            marginTop: 8,
            borderRadius: theme.roundness.md,
            overflow: 'hidden',
        } : {}),
    }),
    actions: (variant) => ({
        flexDirection: 'row',
        alignItems: "center",
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6,
        paddingTop: 10,
        marginTop: 12,
        borderTopWidth: variant === 'alternate' ? 0 : 1,
        borderTopColor: theme.colors.borderSubtle,
    }),
    actionsLeft: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        minWidth: 0,
    },
    reactionsGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    addReactionButton: {
        minHeight: 30,
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 100,
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingVertical: 5,
        paddingHorizontal: 10,
        marginHorizontal: 2,
        marginVertical: 4,
        _web: { _hover: { backgroundColor: theme.colors.hover } },
    },
    actionLabel: {
        color: theme.colors.textSecondary,
        fontSize: theme.typography.micro,
        fontFamily: theme.fonts.bold,
    },
    commentPill: {
        minHeight: 30,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 100,
        paddingVertical: 5,
        paddingHorizontal: 10,
        marginHorizontal: 2,
        marginVertical: 4,
        _web: { _hover: { backgroundColor: theme.colors.hover } },
    },
    emptyPill: {
        opacity: 0.8,
    },
    commentCount: {
        fontSize: theme.typography.micro,
        color: theme.colors.textSecondary,
        paddingLeft: 6,
        fontFamily: theme.fonts.heading,
    },
    topRightContainer: (isCollapsed) => ({
        position: "absolute",
        top: isCollapsed ? 0 : 14,
        bottom: isCollapsed ? 0 : undefined,
        right: rt.breakpoint === 'xs' ? 14 : 20,
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
    }),
}))

export default React.memo(Post);
